import {
    animate,
    AnimationBuilder,
    AnimationPlayer,
    style,
} from '@angular/animations';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
    CommonModule,
    DOCUMENT,
    NgFor,
    NgIf,
    NgOptimizedImage,
} from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    inject,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import {
    FuseNavigationItem,
    FuseVerticalNavigationAppearance,
    FuseVerticalNavigationMode,
    FuseVerticalNavigationPosition,
} from '@fuse/components/navigation/navigation.types';
import { FuseVerticalNavigationAsideItemComponent } from '@fuse/components/navigation/vertical/components/aside/aside.component';
import { FuseVerticalNavigationBasicItemComponent } from '@fuse/components/navigation/vertical/components/basic/basic.component';
import { FuseVerticalNavigationCollapsableItemComponent } from '@fuse/components/navigation/vertical/components/collapsable/collapsable.component';
import { FuseVerticalNavigationDividerItemComponent } from '@fuse/components/navigation/vertical/components/divider/divider.component';
import { FuseVerticalNavigationGroupItemComponent } from '@fuse/components/navigation/vertical/components/group/group.component';
import { FuseVerticalNavigationSpacerItemComponent } from '@fuse/components/navigation/vertical/components/spacer/spacer.component';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar/scrollbar.directive';
import { FuseUtilsService } from '@fuse/services/utils/utils.service';
import { LinkService } from 'app/_helpers/link-service.service';
import { clubConfig } from 'config/club.config';
import { parrainConfig } from 'config/parrain.config';
import { SidebarComponent } from 'app/components/advertising/sidebar/sidebar.component';
import { LogoParrainComponent } from 'app/components/logo-parrain/logo-parrain.component';
import { defaultNavigation } from '../../../../app/mock-api/common/navigation/data';
import { StorageService } from '../../../../app/modules/auth/services/storage.service';
import { User } from 'app/core/user/user.types';
import { Role } from '../../../../app/modules/auth/services/role.types';
import {
    BehaviorSubject,
    delay,
    filter,
    merge,
    Observable,
    ReplaySubject,
    Subject,
    Subscription,
    takeUntil,
} from 'rxjs';
import Parrain from 'config/parrain';
import { HierarchyTeamService } from '../../../../app/_services/hierarchy-team.service';
import { TranslocoModule } from '@ngneat/transloco';
import { AdvertisingCampaignService } from 'app/modules/admin/advertising-campaign/services/advertising.service';
import { CONSTANTS } from 'app/_helpers/constants';
import { cloneDeep } from 'lodash-es';
import { ParrainAnnonceService } from 'app/_services/parrain-annonce.service';
@Component({
    selector: 'fuse-vertical-navigation',
    templateUrl: './vertical.component.html',
    styleUrls: ['./vertical.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'fuseVerticalNavigation',
    standalone: true,
    imports: [
        CommonModule,
        FuseScrollbarDirective,
        FuseVerticalNavigationAsideItemComponent,
        FuseVerticalNavigationBasicItemComponent,
        FuseVerticalNavigationCollapsableItemComponent,
        FuseVerticalNavigationDividerItemComponent,
        FuseVerticalNavigationGroupItemComponent,
        FuseVerticalNavigationSpacerItemComponent,
        LogoParrainComponent,
        MatButtonModule,
        MatIconModule,
        RouterModule,
        TranslocoModule,
    ],
})
export class FuseVerticalNavigationComponent
    implements OnChanges, OnInit, AfterViewInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_inner: BooleanInput;
    static ngAcceptInputType_opened: BooleanInput;
    static ngAcceptInputType_transparentOverlay: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() appearance: FuseVerticalNavigationAppearance = 'default';
    @Input() autoCollapse: boolean = true;
    @Input() inner: boolean = false;
    @Input() mode: FuseVerticalNavigationMode = 'side';
    @Input() name: string = this._fuseUtilsService.randomId();
    @Input() navigation: FuseNavigationItem[];
    @Input() opened: boolean = true;
    @Input() position: FuseVerticalNavigationPosition = 'left';
    @Input() transparentOverlay: boolean = false;
    @Output()
    readonly appearanceChanged: EventEmitter<FuseVerticalNavigationAppearance> =
        new EventEmitter<FuseVerticalNavigationAppearance>();
    @Output() readonly modeChanged: EventEmitter<FuseVerticalNavigationMode> =
        new EventEmitter<FuseVerticalNavigationMode>();
    @Output() readonly openedChanged: EventEmitter<boolean> =
        new EventEmitter<boolean>();
    @Output()
    readonly positionChanged: EventEmitter<FuseVerticalNavigationPosition> =
        new EventEmitter<FuseVerticalNavigationPosition>();
    @ViewChild('navigationContent') private _navigationContentEl: ElementRef;

    activeAsideItemId: string | null = null;
    onCollapsableItemCollapsed: ReplaySubject<FuseNavigationItem> =
        new ReplaySubject<FuseNavigationItem>(1);
    onCollapsableItemExpanded: ReplaySubject<FuseNavigationItem> =
        new ReplaySubject<FuseNavigationItem>(1);
    onRefreshed: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private _animationsEnabled: boolean = false;
    private _asideOverlay: HTMLElement;
    private readonly _handleAsideOverlayClick: any;
    private readonly _handleOverlayClick: any;
    private _hovered: boolean = false;
    private _mutationObserver: MutationObserver;
    private _overlay: HTMLElement;
    private _player: AnimationPlayer;

    private _scrollStrategy: ScrollStrategy =
        this._scrollStrategyOptions.block();
    private _fuseScrollbarDirectives!: QueryList<FuseScrollbarDirective>;
    private _fuseScrollbarDirectivesSubscription: Subscription;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private clubId: string;
    private storageService = inject(StorageService);
    private hierarchyTeamService = inject(HierarchyTeamService);
    private isParrain: boolean = false;
    private _filteredNavigation = new BehaviorSubject<FuseNavigationItem[]>(
        defaultNavigation
    );
    public filteredNavigation$ = this._filteredNavigation.asObservable();
    private hasParrainRole: boolean = false;
    club = clubConfig[0];
    // parrain = parrainConfig[0];
    showAdvertising: boolean;
    //TODO récupérer les infos teamsport 2000 pour les id club et ajouter au fichier config urlBoutique pour rendre dynamique le lien url
    url = 'https://fcversoix.ch/boutique/';
    private _linkService = inject(LinkService);
    userCurrentInfo = null;
    rolesUserCurrent = null;
    /**
     * Constructor
     */
    constructor(
        private _animationBuilder: AnimationBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        @Inject(DOCUMENT) private _document: Document,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _router: Router,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseUtilsService: FuseUtilsService,
        private campaignService: AdvertisingCampaignService,
        private parrainAnnonceService: ParrainAnnonceService
    ) {
        this.userCurrentInfo = this.storageService.getItem<User>('user');
        this.rolesUserCurrent = this.storageService.getItem<Role[]>('roles');

        this.filteredNavigation = this.filterNavigation(
            defaultNavigation,
            this.getUserLevel()
        );
        this.filteredNavigation = this.filterNavigationByRole(
            this.filteredNavigation,
            this.getUserRoles()
        );
        this._handleAsideOverlayClick = (): void => {
            this.closeAside();
        };
        this._handleOverlayClick = (): void => {
            this.close();
        };
    }
    get filteredNavigation(): FuseNavigationItem[] {
        return this._filteredNavigation.getValue();
    }

    set filteredNavigation(value: FuseNavigationItem[]) {
        this._filteredNavigation.next(value);
    }
    /**
     * code pour exact level et plus
     */
    private hasAccess(requiredLevel: number): boolean {
        const user = this.getUserCurrentInfo();
        const roles = this.getRolesUserCurrentInfo();

        if (!user || !roles) {
            return false;
        }

        return user.role.some((role) => {
            const level = roles.find((r) => r.name === role)?.level || 0;
            return level >= requiredLevel;
        });
    }
    /**
     * code pour exact level
     */
    private hasExactRole(requiredLevel: number): boolean {
        const user = this.getUserCurrentInfo();
        const roles = this.getUserCurrentInfo();
        console.log('rolesaaaaaaaa', roles);
        if (!user || !roles) {
            return false;
        }

        return user.role.some((role) => {
            const level = roles.find((r) => r.name === role)?.level || 0;
            return level === requiredLevel;
        });
    }

    /**
     *  Filtrer la navigation en fonction du niveau utilisateur
     * ps: pour ajouter un filter va au dossier src/app/mock-api/common/navigation/data
     * et ajout ex:   //   level: 100000 ,
     */
    private filterNavigation(
        navigation: FuseNavigationItem[],
        userLevel: number
    ): FuseNavigationItem[] {
        return navigation
            .map((item) => ({
                ...item,
                children: item.children
                    ? this.filterNavigation(item.children, userLevel)
                    : undefined,
            }))
            .filter((item) => {
                if (item.exactLevel !== undefined) {
                    return userLevel === item.exactLevel; // Affiche uniquement si `userLevel` est EXACTEMENT égal à `exactLevel`
                }
                if (item.level !== undefined) {
                    return userLevel >= item.level; // Affiche si `userLevel` est supérieur ou égal à `level`
                }
                return true; // Si pas de niveau défini, tout le monde peut voir
            });
    }

    private filterNavigationByRole(
        navigation: FuseNavigationItem[],
        userRoles: string[]
    ): FuseNavigationItem[] {
        return navigation
            .map((item) => ({
                ...item,
                children: item.children
                    ? this.filterNavigationByRole(item.children, userRoles)
                    : undefined,
            }))
            .filter((item) => {
                // Si pas de restriction de rôle, visible par tous
                if (!item.roles) {
                    return true;
                }

                // Vérifie si l'utilisateur a au moins un des rôles requis
                return item.roles.some((requiredRole) =>
                    userRoles.includes(requiredRole)
                );
            });
    }

    /**
     * Récupère les informations de l'utilisateur depuis le localStorage
     */
    private getUserLevel(): number {
        const user = this.getUserCurrentInfo();
        const roles = this.getRolesUserCurrentInfo();
        if (!user || !roles) {
            console.log(' Aucun utilisateur ou rôle trouvé dans le stockage.');
            return 0;
        }

        return user.roles
            .map(
                (roleName) => roles.find((r) => r.name === roleName)?.level || 0
            )
            .reduce((max, level) => Math.max(max, level), 0);
    }

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            'fuse-vertical-navigation-animations-enabled':
                this._animationsEnabled,
            [`fuse-vertical-navigation-appearance-${this.appearance}`]: true,
            'fuse-vertical-navigation-hover': this._hovered,
            'fuse-vertical-navigation-inner': this.inner,
            'fuse-vertical-navigation-mode-over': this.mode === 'over',
            'fuse-vertical-navigation-mode-side': this.mode === 'side',
            'fuse-vertical-navigation-opened': this.opened,
            'fuse-vertical-navigation-position-left': this.position === 'left',
            'fuse-vertical-navigation-position-right':
                this.position === 'right',
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    /**
     * Host binding for component inline styles
     */
    @HostBinding('style') get styleList(): any {
        return {
            visibility: this.opened ? 'visible' : 'hidden',
        };
    }

    /**
     * Setter for fuseScrollbarDirectives
     */
    @ViewChildren(FuseScrollbarDirective)
    set fuseScrollbarDirectives(
        fuseScrollbarDirectives: QueryList<FuseScrollbarDirective>
    ) {
        // Store the directives
        this._fuseScrollbarDirectives = fuseScrollbarDirectives;

        // Return if there are no directives
        if (fuseScrollbarDirectives.length === 0) {
            return;
        }

        // Unsubscribe the previous subscriptions
        if (this._fuseScrollbarDirectivesSubscription) {
            this._fuseScrollbarDirectivesSubscription.unsubscribe();
        }

        // Update the scrollbars on collapsable items' collapse/expand
        this._fuseScrollbarDirectivesSubscription = merge(
            this.onCollapsableItemCollapsed,
            this.onCollapsableItemExpanded
        )
            .pipe(takeUntil(this._unsubscribeAll), delay(250))
            .subscribe(() => {
                // Loop through the scrollbars and update them
                fuseScrollbarDirectives.forEach((fuseScrollbarDirective) => {
                    fuseScrollbarDirective.update();
                });
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On mouseenter
     *
     * @private
     */
    @HostListener('mouseenter')
    private _onMouseenter(): void {
        // Enable the animations
        this._enableAnimations();

        this._showAdvertising();
        // Set the hovered
        this._hovered = true;
    }

    /**
     * On mouseleave
     *
     * @private
     */
    @HostListener('mouseleave')
    private _onMouseleave(): void {
        // Enable the animations
        this._enableAnimations();

        this._hideAdvertising();

        // Set the hovered
        this._hovered = false;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    // ? Pour navigation externe avec ionic et postmessage

    // sendMessage(data: any) {
    //     const message = JSON.stringify(data);
    //     window.postMessage(message, '*'); // Utilisez '*' pour l'exemple, mais spécifiez l'origine si possible pour la sécurité
    // }

    // // Exemple de méthode dans un composant Angular pour ouvrir un lien externe

    // private openExternalLink(url: string): void {
    //     // window.open(`ionic://open?url=${encodeURIComponent(url)}`, '_self');

    //     // Ici, nous utiliserons Capacitor pour ouvrir le lien
    //     // window.location.href = `ionic://${url}`;
    //     // console.log('Ouverture du lien externe : ', url);
    //     this.openLinkInNativeView(url);
    // }

    // private async openLinkInNativeView(url: string): Promise<void> {
    //     await Browser.open({
    //         url,
    //         windowName: '_self',
    //         presentationStyle: 'fullscreen',
    //         toolbarColor: 'blue',
    //     });
    // }

    onClick() {
        this._linkService.sendMessage({ url: this.url });
        this._linkService.openLinkInNativeView(this.url);
    }

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Appearance
        if ('appearance' in changes) {
            // Execute the observable
            this.appearanceChanged.next(changes.appearance.currentValue);
        }

        // Inner
        if ('inner' in changes) {
            // Coerce the value to a boolean
            this.inner = coerceBooleanProperty(changes.inner.currentValue);
        }

        // Mode
        if ('mode' in changes) {
            // Get the previous and current values
            const currentMode = changes.mode.currentValue;
            const previousMode = changes.mode.previousValue;

            // Disable the animations
            this._disableAnimations();

            // If the mode changes: 'over -> side'
            if (previousMode === 'over' && currentMode === 'side') {
                // Hide the overlay
                this._hideOverlay();
            }

            // If the mode changes: 'side -> over'
            if (previousMode === 'side' && currentMode === 'over') {
                // Close the aside
                this.closeAside();

                // If the navigation is opened
                if (this.opened) {
                    // Show the overlay
                    this._showOverlay();
                }
            }

            // Execute the observable
            this.modeChanged.next(currentMode);

            // Enable the animations after a delay
            // The delay must be bigger than the current transition-duration
            // to make sure nothing will be animated while the mode changing
            setTimeout(() => {
                this._enableAnimations();
            }, 500);
        }

        // Navigation
        if ('navigation' in changes) {
            // Mark for check
            this._changeDetectorRef.markForCheck();
        }

        // Opened
        if ('opened' in changes) {
            // Coerce the value to a boolean
            this.opened = coerceBooleanProperty(changes.opened.currentValue);

            // Open/close the navigation
            this._toggleOpened(this.opened);
        }

        // Position
        if ('position' in changes) {
            // Execute the observable
            this.positionChanged.next(changes.position.currentValue);
        }

        // Transparent overlay
        if ('transparentOverlay' in changes) {
            // Coerce the value to a boolean
            this.transparentOverlay = coerceBooleanProperty(
                changes.transparentOverlay.currentValue
            );
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        //recuperer les datta de storageService
        const userInfo = this.getUserCurrentInfo();
        const allRoles = this.getRolesUserCurrentInfo();
        this.hasParrainRole =
            userInfo?.roles?.some((role) => {
                const roleData = allRoles?.find((r) => r.name === role);
                return roleData?.level === CONSTANTS.LEVEL_SPONSOR;
            }) ?? false;
        this.checkIfMainSponsorStatus();
        // Initialiser la navigation de base
        this.initializeBaseNavigation();

        // Écouter les changements d'équipes
        this.subscribeToTeamsChanges();

        // Écouter les changements d'équipes publiques
        this.subscribeToPublicTeamsChanges();

        // Mettre à jour avec les profils d'enfants
        this.updateNavigationWithChildren();

        // Autres initialisations existantes...

        this.initializeOtherFeatures();
    }
    /**
     * Initialise la navigation de base avec filtrage par niveau et rôle
     */
    private initializeBaseNavigation(): void {
        const userLevel = this.getUserLevel();
        const userRoles = this.getUserRoles();

        // Filtrer la navigation par niveau et rôle
        let filteredNav = this.filterNavigation(defaultNavigation, userLevel);
        filteredNav = this.filterNavigationByRole(filteredNav, userRoles);

        this.filteredNavigation = filteredNav;
    }

    /**
     * S'abonne aux changements d'équipes pour "Mon Club"
     */
    private subscribeToTeamsChanges(): void {
        const user = this.getUserCurrentInfo();
        if (!user) return;

        // Déterminer quel observable utiliser selon le rôle
        let teamsObservable: Observable<FuseNavigationItem[]>;

        if (user.roles?.includes('Parent')) {
            teamsObservable =
                this.hierarchyTeamService.getPlayerAndChildrenTeams();
        } else if (user.roles?.includes('Player')) {
            teamsObservable = this.hierarchyTeamService.getPlayerTeams();
        } else {
            teamsObservable = this.hierarchyTeamService.teams$;
        }

        // S'abonner aux changements d'équipes
        teamsObservable
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((teams) => {
                this.updateNavigationWithTeams(teams, 'mon_club');
            });
    }


    /**
     * S'abonne aux changements d'équipes pour "Mon Club"
     */
    private subscribeToTeams(): void {
        // Déterminer quel observable utiliser selon le rôle
        let teamsObservable: Observable<FuseNavigationItem[]>;


        teamsObservable = this.hierarchyTeamService.teams$;


        // S'abonner aux changements d'équipes
        teamsObservable
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((teams) => {
                this.updateNavigationWithTeams(teams, 'mon_club');
            });
    }

    /**
     * S'abonne aux changements d'équipes publiques
     */
    private subscribeToPublicTeamsChanges(): void {
        this.hierarchyTeamService.publicTeams$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((publicTeams) => {
                this.updateNavigationWithTeams(
                    publicTeams,
                    'equipes_publiques'
                );
            });
    }

    /**
     * Met à jour la navigation avec les profils d'enfants
     */
    private updateNavigationWithChildren(): void {
        const user = this.getUserCurrentInfo();
        if (!user?.roles?.includes('Parent')) {
            return;
        }

        this.hierarchyTeamService
            .getChildrenProfiles(user.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (children) => {
                    this.updateChildrenProfiles(children);
                },
                error: (err) => {
                    console.error(
                        'Erreur lors de la récupération des enfants:',
                        err
                    );
                },
            });
    }

    /**
     * Met à jour les profils d'enfants dans la navigation
     */
    private updateChildrenProfiles(children: FuseNavigationItem[]): void {
        const currentNav = cloneDeep(this.filteredNavigation);
        const monClubItem = currentNav.find((item) => item.id === 'mon_club');

        if (monClubItem?.children) {
            let childrenItem = monClubItem.children.find(
                (item) => item.id === 'children-profiles'
            );

            if (!childrenItem) {
                childrenItem = {
                    id: 'children-profiles',
                    title: 'Mes enfants',
                    type: 'collapsable',
                    icon: 'heroicons_mini:user-group',
                    children: [],
                    roles: ['Parent'],
                };
                monClubItem.children.push(childrenItem);
            }

            // Mettre à jour les enfants
            childrenItem.children = [...children];

            // Mettre à jour la navigation via BehaviorSubject
            this.filteredNavigation = currentNav;
            this._changeDetectorRef.detectChanges();
        }
    }

    /**
     * Met à jour la navigation avec les équipes
     */
    private updateNavigationWithTeams(
        teams: FuseNavigationItem[],
        targetId: string
    ): void {
        const currentNav = cloneDeep(this.filteredNavigation);
        let updated = false;

        if (targetId === 'mon_club') {
            // Mettre à jour les équipes dans "Mon Club"
            const monClubItem = currentNav.find(
                (item) => item.id === 'mon_club'
            );
            if (monClubItem?.children) {
                const equipesItem = monClubItem.children.find(
                    (item) => item.id === 'equipes'
                );
                if (equipesItem) {
                    equipesItem.children = [...teams];
                    updated = true;
                }

                // Mettre à jour "Mes Équipes" pour les joueurs
                const user = this.getUserCurrentInfo();
                if (user?.roles?.includes('Player')) {
                    let mesEquipesItem = monClubItem.children.find(
                        (item) => item.id === 'mesequipes'
                    );
                    if (mesEquipesItem) {
                        mesEquipesItem.children = [...teams];
                        updated = true;
                    }
                }
            }
        } else if (targetId === 'equipes_publiques') {
            // Mettre à jour les équipes publiques
            const equipesPubliquesItem = currentNav.find(
                (item) => item.id === 'equipes_publiques'
            );
            if (equipesPubliquesItem) {
                equipesPubliquesItem.children = [...teams];
                updated = true;
            }
        }

        if (updated) {
            this.filteredNavigation = currentNav;
            this._changeDetectorRef.detectChanges();
        }
    }

    /**
     * Initialise les autres fonctionnalités (publicité, etc.)
     */
    private initializeOtherFeatures(): void {
        // Nom du composant
        if (this.name === '') {
            this.name = this._fuseUtilsService.randomId();
        }

        // Enregistrer le composant
        this._fuseNavigationService.registerComponent(this.name, this);

        // S'abonner aux événements de navigation
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                if (this.mode === 'over' && this.opened) {
                    this.close();
                }
                if (this.mode === 'side' && this.activeAsideItemId) {
                    this.closeAside();
                }
            });

        // Initialiser les campagnes publicitaires
        this.initializeAdvertisingCampaigns();
    }

    /**
     * Initialise les campagnes publicitaires
     */
    private initializeAdvertisingCampaigns(): void {
        this.clubId = this.getUserCurrentInfo()?.activeClubId;

        if (this.clubId) {
            this.campaignService
                .getPublicAdvertisingVisibility(this.clubId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (response) => {
                        const isShowAdvertising = response?.is_show_advertising;
                        const userLevel = this.getUserLevel();

                        // Filtrer les campagnes
                        const currentNav = cloneDeep(this.filteredNavigation);
                        const filteredNav = currentNav.filter((item) => {
                            if (item.id === 'campaign') {
                                return this.shouldShowCampaign(
                                    userLevel,
                                    isShowAdvertising
                                );
                            }
                            return true;
                        });

                        this.filteredNavigation = filteredNav;
                        this._changeDetectorRef.detectChanges();
                    },
                    error: (err) => {
                        console.error(
                            'Erreur récupération publicité club :',
                            err
                        );
                    },
                });
        }
    }
    private getUserRoles(): string[] {
        const user = this.getUserCurrentInfo();
        return user?.roles || [];
    }
    /**
     * After view init
     */
    ngAfterViewInit(): void {
        setTimeout(() => {
            this.subscribeToTeams();
        },150);
        // Fix for Firefox.
        //
        // Because 'position: sticky' doesn't work correctly inside a 'position: fixed' parent,
        // adding the '.cdk-global-scrollblock' to the html element breaks the navigation's position.
        // This fixes the problem by reading the 'top' value from the html element and adding it as a
        // 'marginTop' to the navigation itself.
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const mutationTarget = mutation.target as HTMLElement;
                if (mutation.attributeName === 'class') {
                    if (
                        mutationTarget.classList.contains(
                            'cdk-global-scrollblock'
                        )
                    ) {
                        const top = parseInt(mutationTarget.style.top, 10);
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            `${Math.abs(top)}px`
                        );
                    } else {
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            null
                        );
                    }
                }
            });
        });
        this._mutationObserver.observe(this._document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        setTimeout(() => {
            // Return if 'navigation content' element does not exist
            if (!this._navigationContentEl) {
                return;
            }

            // If 'navigation content' element doesn't have
            // perfect scrollbar activated on it...
            if (
                !this._navigationContentEl.nativeElement.classList.contains(
                    'ps'
                )
            ) {
                // Find the active item
                const activeItem =
                    this._navigationContentEl.nativeElement.querySelector(
                        '.fuse-vertical-navigation-item-active'
                    );

                // If the active item exists, scroll it into view
                if (activeItem) {
                    activeItem.scrollIntoView();
                }
            }
            // Otherwise
            else {
                // Go through all the scrollbar directives
                this._fuseScrollbarDirectives.forEach(
                    (fuseScrollbarDirective) => {
                        // Skip if not enabled
                        if (!fuseScrollbarDirective.isEnabled()) {
                            return;
                        }

                        // Scroll to the active element
                        fuseScrollbarDirective.scrollToElement(
                            '.fuse-vertical-navigation-item-active',
                            -120,
                            true
                        );
                    }
                );
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Disconnect the mutation observer
        this._mutationObserver.disconnect();

        // Forcefully close the navigation and aside in case they are opened
        this.close();
        this.closeAside();

        // Deregister the navigation component from the registry
        this._fuseNavigationService.deregisterComponent(this.name);

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Refresh the component to apply the changes
     */
    refresh(): void {
        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Execute the observable
        this.onRefreshed.next(true);
    }

    /**
     * Open the navigation
     */
    open(): void {
        this._showAdvertising();
        // Return if the navigation is already open
        if (this.opened) {
            return;
        }

        // Set the opened
        this._toggleOpened(true);
    }

    /**
     * Close the navigation
     */
    close(): void {
        this._hideAdvertising();
        // Return if the navigation is already closed
        if (!this.opened) {
            return;
        }

        // Close the aside
        this.closeAside();

        // Set the opened
        this._toggleOpened(false);
    }

    /**
     * Toggle the navigation
     */
    toggle(): void {
        // Toggle
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Open the aside
     *
     * @param item
     */
    openAside(item: FuseNavigationItem): void {
        // Return if the item is disabled
        if (item.disabled || !item.id) {
            return;
        }

        // Open
        this.activeAsideItemId = item.id;

        // Show the aside overlay
        this._showAsideOverlay();

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Close the aside
     */
    closeAside(): void {
        // Close
        this.activeAsideItemId = null;

        // Hide the aside overlay
        this._hideAsideOverlay();

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Toggle the aside
     *
     * @param item
     */
    toggleAside(item: FuseNavigationItem): void {
        // Toggle
        if (this.activeAsideItemId === item.id) {
            this.closeAside();
        } else {
            this.openAside(item);
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Enable the animations
     *
     * @private
     */
    private _enableAnimations(): void {
        // Return if the animations are already enabled
        if (this._animationsEnabled) {
            return;
        }

        // Enable the animations
        this._animationsEnabled = true;
    }

    /**
     * Disable the animations
     *
     * @private
     */
    private _disableAnimations(): void {
        // Return if the animations are already disabled
        if (!this._animationsEnabled) {
            return;
        }

        // Disable the animations
        this._animationsEnabled = false;
    }

    /**
     * Show the overlay
     *
     * @private
     */
    private _showOverlay(): void {
        // Return if there is already an overlay
        if (this._asideOverlay) {
            return;
        }

        // Create the overlay element
        this._overlay = this._renderer2.createElement('div');

        // Add a class to the overlay element
        this._overlay.classList.add('fuse-vertical-navigation-overlay');

        // Add a class depending on the transparentOverlay option
        if (this.transparentOverlay) {
            this._overlay.classList.add(
                'fuse-vertical-navigation-overlay-transparent'
            );
        }

        // Append the overlay to the parent of the navigation
        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._overlay
        );

        // Enable block scroll strategy
        this._scrollStrategy.enable();

        // Create the enter animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 1 })
                ),
            ])
            .create(this._overlay);

        // Play the animation
        this._player.play();

        // Add an event listener to the overlay
        this._overlay.addEventListener('click', this._handleOverlayClick);
    }

    /**
     * Hide the overlay
     *
     * @private
     */
    private _hideOverlay(): void {
        if (!this._overlay) {
            return;
        }

        // Create the leave animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 0 })
                ),
            ])
            .create(this._overlay);

        // Play the animation
        this._player.play();

        // Once the animation is done...
        this._player.onDone(() => {
            // If the overlay still exists...
            if (this._overlay) {
                // Remove the event listener
                this._overlay.removeEventListener(
                    'click',
                    this._handleOverlayClick
                );

                // Remove the overlay
                this._overlay.parentNode.removeChild(this._overlay);
                this._overlay = null;
            }

            // Disable block scroll strategy
            this._scrollStrategy.disable();
        });
    }

    /**
     * Show the aside overlay
     *
     * @private
     */
    private _showAsideOverlay(): void {
        // Return if there is already an overlay
        if (this._asideOverlay) {
            return;
        }

        // Create the aside overlay element
        this._asideOverlay = this._renderer2.createElement('div');

        // Add a class to the aside overlay element
        this._asideOverlay.classList.add(
            'fuse-vertical-navigation-aside-overlay'
        );

        // Append the aside overlay to the parent of the navigation
        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._asideOverlay
        );

        // Create the enter animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 1 })
                ),
            ])
            .create(this._asideOverlay);

        // Play the animation
        this._player.play();

        // Add an event listener to the aside overlay
        this._asideOverlay.addEventListener(
            'click',
            this._handleAsideOverlayClick
        );
    }

    /**
     * Hide the aside overlay
     *
     * @private
     */
    private _hideAsideOverlay(): void {
        if (!this._asideOverlay) {
            return;
        }

        // Create the leave animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 0 })
                ),
            ])
            .create(this._asideOverlay);

        // Play the animation
        this._player.play();

        // Once the animation is done...
        this._player.onDone(() => {
            // If the aside overlay still exists...
            if (this._asideOverlay) {
                // Remove the event listener
                this._asideOverlay.removeEventListener(
                    'click',
                    this._handleAsideOverlayClick
                );

                // Remove the aside overlay
                this._asideOverlay.parentNode.removeChild(this._asideOverlay);
                this._asideOverlay = null;
            }
        });
    }

    /**
     * Open/close the navigation
     *
     * @param open
     * @private
     */
    private _toggleOpened(open: boolean): void {
        // Set the opened
        this.opened = open;

        // Enable the animations
        this._enableAnimations();

        // If the navigation opened, and the mode
        // is 'over', show the overlay
        if (this.mode === 'over') {
            if (this.opened) {
                this._showAdvertising();
                this._showOverlay();
            } else {
                this._hideAdvertising();
                this._hideOverlay();
            }
        }

        // Execute the observable
        this.openedChanged.next(open);
    }

    _hideAdvertising(): void {
        this.showAdvertising = false;
    }

    _showAdvertising(): void {
        this.showAdvertising = true;
    }

    // onShowParrainSpace(parrainId: string) {
    //     console.log('Navigation vers partenaire ID:', parrainId);

    //     this._router.navigate(['/partenaire-detail', parrainId]).then(() => {
    //         this._hideAsideOverlay();
    //         this._hideOverlay();
    //         this._changeDetectorRef.markForCheck();
    //     });
    // }
    onShowParrainSpace(parrainId: string): void {
        this.parrainAnnonceService.getAnnonceApprouveeByParrainId(parrainId).subscribe({
            next: (annonce: any) => {
                let route: any[];

                if (annonce) {
                    console.log('***************** annonce trouvée');
                    route = ['/active-announcement', annonce.id];
                } else {
                    console.log('!!!!!!!!!!!!!!!!! aucune annonce');
                    route = ['/partenaire-detail', parrainId];
                }

                this._router.navigate(route).then(() => {
                    this._hideAsideOverlay();
                    this._hideOverlay();
                    this._changeDetectorRef.markForCheck();
                });
            },
            error: () => {
                console.log('!!!!!!!!!!!!!!!!! erreur de récupération annonce');
                this._router.navigate(['/partenaire-detail', parrainId]).then(() => {
                    this._hideAsideOverlay();
                    this._hideOverlay();
                    this._changeDetectorRef.markForCheck();
                });
            }
        });
    }


    private shouldShowCampaign(
        userLevel: number,
        isShowAdvertising: boolean
    ): boolean {
        // Tous les utilisateurs à partir du niveau GUEST
        if (isShowAdvertising && userLevel >= CONSTANTS.LEVEL_GUEST) {
            return true;
        }

        // Seulement Community Manager et plus si la pub n’est pas affichée
        if (
            !isShowAdvertising &&
            userLevel >= CONSTANTS.LEVEL_COMMUNITY_MANAGER
        ) {
            return true;
        }

        return false;
    }

    // private getUserRoles(): string[] {
    //     const user = this.storageService.getItem<User>('user');
    //     return user?.roles || [];
    // }

    private setupNavigation(): void {
        const userLevel = this.getUserLevel();
        const userRoles = this.getUserRoles();

        const allNavigation = this.filterNavigation(
            defaultNavigation,
            userLevel
        );
        const byRole = this.filterNavigationByRole(allNavigation, userRoles);

        this.campaignService
            .getPublicAdvertisingVisibility(this.clubId)
            .subscribe({
                next: (response) => {
                    const isShowAdvertising = response?.is_show_advertising;

                    this.filteredNavigation = byRole.filter((item) => {
                        if (item.id === 'campaign') {
                            return this.shouldShowCampaign(
                                userLevel,
                                isShowAdvertising
                            );
                        }

                        if (item.id === 'espace-parrain') {
                            const isCommunityManager =
                                userLevel >= CONSTANTS.LEVEL_COMMUNITY_MANAGER;
                            const isParrainSponsor =
                                this.hasParrainRole && this.isParrain;

                            return isCommunityManager || isParrainSponsor;
                        }

                        return true;
                    });

                    this._changeDetectorRef.detectChanges();
                },
                error: (err) => {
                    console.error('Erreur récupération publicité club :', err);
                },
            });
    }
    private checkIfMainSponsorStatus(): void {
        const user = this.getUserCurrentInfo();
        if (!user?.email) return;

        this.parrainAnnonceService
            .checkIfMainSponsor(user.email)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res) => {
                    this.isParrain = res?.is_main_sponsor === true;
                    // Ne pas appeler setupNavigation() ici, car cela refiltre toute la navigation
                    this.updateEspaceParrainVisibility();
                },
                error: () => {
                    this.isParrain = false;
                    this.updateEspaceParrainVisibility();
                },
            });
    }

    private updateEspaceParrainVisibility(): void {
        const currentNav = cloneDeep(this.filteredNavigation);
        const espaceParrainItem = currentNav.find(item => item.id === 'espace-parrain');

        if (espaceParrainItem) {
            const userLevel = this.getUserLevel();
            const isCommunityManager = userLevel >= CONSTANTS.LEVEL_COMMUNITY_MANAGER;
            const isParrainSponsor = this.hasParrainRole && this.isParrain;

            // Filtrez seulement l'item espace-parrain si nécessaire
            if (!isCommunityManager && !isParrainSponsor) {
                this.filteredNavigation = currentNav.filter(item => item.id !== 'espace-parrain');
            } else if (isCommunityManager || isParrainSponsor) {
                if (!currentNav.some(item => item.id === 'espace-parrain')) {
                    // Ajoutez l'item s'il n'existe pas déjà
                    const espaceParrain = defaultNavigation.find(item => item.id === 'espace-parrain');
                    if (espaceParrain) {
                        currentNav.push(espaceParrain);
                        this.filteredNavigation = currentNav;
                    }
                }
            }
            this._changeDetectorRef.detectChanges();
        }
    }

    private getUserCurrentInfo() {
        return !this.userCurrentInfo ? this.storageService.getItem<User>(`user`) : this.userCurrentInfo;
    }

    private getRolesUserCurrentInfo() {
        return !this.rolesUserCurrent ? this.storageService.getItem<Role[]>('roles') : this.rolesUserCurrent;
    }
}
